<template>
  <div class="pos-ticket-page">
    <div class="pos-ticket-toolbar no-print">
      <button type="button" class="btn-ticket btn-print" @click="printTicket">
        <i class="fas fa-print me-1"></i> Imprimir
      </button>
      <button type="button" class="btn-ticket btn-close-tab" @click="closeTicket">
        <i class="fas fa-times me-1"></i> Cerrar
      </button>
    </div>

    <div v-if="loading" class="pos-ticket-empty no-print">
      <p>Cargando ticket...</p>
    </div>

    <div v-else-if="!ticket" class="pos-ticket-empty no-print">
      <p>No hay datos de ticket para mostrar.</p>
      <button type="button" class="btn-ticket btn-close-tab" @click="closeTicket">Cerrar</button>
    </div>

    <article v-else class="ticket" id="pos-ticket-print">
      <div v-if="logoUrl" class="ticket-logo-wrap">
        <img :src="logoUrl" alt="Logo empresa" class="ticket-logo" />
      </div>
      <header class="ticket-header">
        <h1 class="ticket-company">{{ companyLine }}</h1>
        <p v-if="ticket.company?.nit" class="ticket-sub">NIT: {{ ticket.company.nit }}</p>
        <p class="ticket-sub">Tel.: {{ ticket.company?.phone || "—" }}</p>
      </header>

      <section class="ticket-doc">
        <div class="ticket-sep" style="width: 100%;border-style: dashed;border-width: 0.2px;"></div>
        <div class="ticket-doc-title">Factura de venta</div>
        <div class="ticket-doc-row">
          <span class="ticket-doc-number">No. {{ ticket.invoiceNo }}</span>
          <span class="ticket-doc-date">{{ ticket.dateFormatted }}</span>
        </div>
      </section>

      <section class="ticket-customer">
        <div class="ticket-customer-name">{{ customerName }}</div>
        <div class="ticket-customer-doc">{{ documentLine }}</div>
      </section>

      <section class="ticket-items">
        <div class="ticket-items-head">
          <div class="th-num">#</div>
          <div class="th-qty">Cant.</div>
          <div class="th-desc">
            <span>Descripción</span>
          </div>
          <div class="th-total">
            <span>Valor total</span>
          </div>
        </div>
        <div class="ticket-sep" style="width: 100%;border-style: dashed;border-width: 0.2px;"></div>
        <div
          v-for="(item, index) in ticket.items"
          :key="index"
          class="ticket-item-row"
        >
          <div class="td-num">{{ index + 1 }}</div>
          <div class="td-qty">{{ formatQty(item.quantity) }}</div>
          <div class="td-desc">{{ itemLabel(item) }}</div>
          <div class="td-total">{{ money(item.total) }}</div>
        </div>
      </section>

      <section class="ticket-totals">
        <div class="ticket-totals-block">
          <div class="ticket-total-line ticket-total-main">
            <span class="ticket-total-label">SUBTOTAL ===========&gt;</span>
            <span class="ticket-total-value">{{ money(ticket.subtotal) }}</span>
          </div>
          <div v-if="ticket.discountAmount > 0" class="ticket-total-line">
            <span class="ticket-total-label">DESCUENTO ==&gt;</span>
            <span class="ticket-total-value">−{{ money(ticket.discountAmount) }}</span>
          </div>
          <div v-if="ticket.flete > 0" class="ticket-total-line">
            <span class="ticket-total-label">FLETE ==&gt;</span>
            <span class="ticket-total-value">+{{ money(ticket.flete) }}</span>
          </div>
          <div v-if="ticket.giftValue > 0" class="ticket-total-line">
            <span class="ticket-total-label">OBSEQUIO ==&gt;</span>
            <span class="ticket-total-value">+{{ money(ticket.giftValue) }}</span>
          </div>
          <div class="ticket-total-line ticket-total-main">
            <span class="ticket-total-label">TOTAL ===========&gt;</span>
            <span class="ticket-total-value">{{ money(ticket.total) }}</span>
          </div>
          <div class="ticket-total-line">
            <span class="ticket-total-label">RECIBIDO ==&gt;</span>
            <span class="ticket-total-value">{{ money(ticket.received) }}</span>
          </div>
          <div class="ticket-total-line">
            <span class="ticket-total-label">CAMBIO ==&gt;</span>
            <span class="ticket-total-value">{{ money(ticket.change) }}</span>
          </div>
        </div>
      </section>

      <section class="ticket-payment">
        <div class="ticket-payment-head">
          <span>Forma de pago</span>
          <span>Valor</span>
        </div>
        <div class="ticket-payment-row">
          <span>{{ ticket.paymentMethod }}</span>
          <span>{{ money(ticket.received) }}</span>
        </div>
      </section>

      <section class="ticket-footer">
        <div class="ticket-seller">VENDEDOR</div>
        <div class="ticket-footer-block">
          <div class="ticket-footer-line">
            <span>TOTAL :</span>
            <span>{{ money(ticket.total) }}</span>
          </div>
          <div class="ticket-footer-line">
            <span>NRO. ARTICULOS VEND. :</span>
            <span>{{ ticket.articlesCount }}</span>
          </div>
          <div class="ticket-footer-line">
            <span>NRO. ITEMS FACT. :</span>
            <span>{{ ticket.itemsCount }}</span>
          </div>
          <div class="ticket-footer-line">
            <span>USUARIO :</span>
            <span>{{ ticket.seller }}</span>
          </div>
          <div class="ticket-footer-line">
            <span>FECHA :</span>
            <span>{{ ticket.dateFormatted }}</span>
          </div>
        </div>
      </section>

      <footer class="ticket-legal">
        <div>Impreso desde MiStock DKDA</div>
      </footer>
    </article>
  </div>
</template>

<script>
import {
  loadPosTicketData,
  formatTicketMoneyWithSymbol,
  formatTicketDate,
  buildInvoiceNumber,
  mergeTicketCompany,
  resolveCompanyLogoUrl,
} from "@/utils/posTicketPrint";
import settingsService from "@/services/settings.service";
import remissionsService from "@/services/remissions.service";

const body = document.getElementsByTagName("body")[0];

export default {
  name: "PosTicket",
  data() {
    return {
      ticket: null,
      loading: true,
    };
  },
  computed: {
    companyLine() {
      const name = this.ticket?.company?.company_name || "MI STOCK";
      return String(name).toUpperCase();
    },
    customerName() {
      return String(this.ticket?.client?.name || "CONSUMIDOR FINAL").toUpperCase();
    },
    documentLine() {
      const type = this.ticket?.client?.document_type || "CC";
      const doc = this.ticket?.client?.document || "—";
      return `${type} No. : ${doc}`;
    },
    logoUrl() {
      return resolveCompanyLogoUrl(this.ticket?.company);
    },
  },
  methods: {
    money(value) {
      return formatTicketMoneyWithSymbol(value);
    },
    formatQty(qty) {
      const n = Number(qty) || 0;
      return Number.isInteger(n) ? String(n) : n.toLocaleString("es-CO", { maximumFractionDigits: 2 });
    },
    itemLabel(item) {
      const name = String(item.name || "").toUpperCase();
      const suffix = item.ref ? ` (${item.ref})` : " (Und)";
      return name + suffix;
    },
    printTicket() {
      window.print();
    },
    closeTicket() {
      window.close();
      if (!window.closed) {
        this.$router.push({ name: "POS" });
      }
    },
    async fetchCompanySettings() {
      try {
        const settingsRes = await settingsService.get();
        return settingsRes.data || {};
      } catch (e) {
        console.error("No se pudo cargar configuración:", e);
        return {};
      }
    },
    async loadTicket() {
      this.loading = true;
      const remissionId = this.$route.query.id;
      try {
        const company = await this.fetchCompanySettings();
        const cached = loadPosTicketData(remissionId || undefined);
        if (cached) {
          this.ticket = mergeTicketCompany(cached, company);
          return;
        }
        if (remissionId) {
          const res = await remissionsService.getData(remissionId);
          const r = res.data;
          const items = (r.details || []).map((d) => ({
            name: String(d.inventory?.name || "PRODUCTO").toUpperCase(),
            ref: d.inventory?.ref || null,
            quantity: d.quantity,
            unitPrice: d.unit_price,
            total: d.total,
          }));
          const articlesCount = items.reduce((sum, i) => sum + Number(i.quantity || 0), 0);
          const total = Number(r.grand_total || r.total || 0);
          const received = Number(r.amount_paid || total);
          const change = Math.max(0, received - total);
          this.ticket = mergeTicketCompany({
            remissionId: r.id,
            invoiceNo: buildInvoiceNumber(r.id),
            dateFormatted: formatTicketDate(r.created_at),
            client: {
              id: r.user?.id || null,
              name: r.user?.name || r.name || "CONSUMIDOR FINAL",
              document_type: r.user?.document_type || "CC",
              document: r.user?.document || r.document || String(r.user?.id || ""),
            },
            items,
            subtotal: Number(r.subtotal || 0),
            itemsTotal: items.reduce((sum, i) => sum + Number(i.total || 0), 0),
            discountAmount: Number(r.discount_amount || 0),
            flete: Number(r.flete || 0),
            giftValue: Number(r.gift_value || 0),
            total,
            received,
            change,
            paymentMethod: r.payment_method || "EFECTIVO",
            articlesCount,
            itemsCount: items.length,
            seller: String(r.seller?.name || "LOCAL").toUpperCase(),
            company: company || {},
          }, company);
          return;
        }
        this.ticket = null;
      } catch (e) {
        console.error("Error al cargar ticket:", e);
        this.ticket = null;
      } finally {
        this.loading = false;
      }
    },
  },
  async mounted() {
    this.$store.state.hideConfigButton = true;
    this.$store.state.showSidenav = false;
    this.$store.state.showNavbar = false;
    this.$store.state.showFooter = false;
    body.classList.remove("g-sidenav-show");
    await this.loadTicket();
  },
  beforeUnmount() {
    this.$store.state.hideConfigButton = false;
    this.$store.state.showSidenav = true;
    this.$store.state.showNavbar = true;
    this.$store.state.showFooter = true;
    body.classList.add("g-sidenav-show");
  },
};
</script>

<style scoped>
.pos-ticket-page {
  min-height: 100vh;
  background: #e9ecef;
  padding: 1rem;
  display: flex;
  flex-direction: column;
  align-items: center;
}

.pos-ticket-toolbar {
  display: flex;
  gap: 0.75rem;
  margin-bottom: 1rem;
  width: 80mm;
  max-width: 100%;
  justify-content: center;
}

.btn-ticket {
  border: none;
  border-radius: 6px;
  padding: 0.5rem 1.25rem;
  font-size: 0.875rem;
  font-weight: 600;
  cursor: pointer;
  color: #fff;
}

.btn-print {
  background: #82d616;
}

.btn-close-tab {
  background: #67748e;
}

.pos-ticket-empty {
  text-align: center;
  padding: 2rem;
}

.ticket {
  width: 80mm;
  max-width: 100%;
  background: #fff;
  color: #000;
  font-family: Arial, Helvetica, sans-serif;
  font-size: 14px;
  line-height: 1.35;
  padding: 3mm 2mm;
  box-shadow: 0 2px 12px rgba(0, 0, 0, 0.12);
  box-sizing: border-box;
}

.ticket-logo-wrap {
  text-align: center;
  margin-bottom: 0.5rem;
}

.ticket-logo {
  max-width: 56mm;
  max-height: 20mm;
  width: auto;
  height: auto;
  object-fit: contain;
}

.ticket-header {
  text-align: center;
  margin-bottom: 0.5rem;
}

.ticket-company {
  font-size: 14px;
  font-weight: 700;
  margin: 0 0 0.25rem;
  line-height: 1.25;
  text-transform: uppercase;
}

.ticket-sub {
  margin: 0;
  font-size: 14px;
}

.ticket-sep {
  text-align: center;
  letter-spacing: 0;
  margin: 0.35rem 0;
  font-size: 14px;
  overflow: hidden;
  white-space: nowrap;
}

.ticket-doc {
  text-align: center;
  margin-bottom: 0.5rem;
}

.ticket-doc-title {
  font-weight: 700;
  font-size: 14px;
  text-transform: uppercase;
}

.ticket-doc-row {
  display: flex;
  justify-content: space-between;
  font-size: 14px;
  margin-top: 0.25rem;
}

.ticket-customer {
  margin-bottom: 0.5rem;
}

.ticket-customer-name {
  font-weight: 700;
  font-size: 14px;
  text-transform: uppercase;
}

.ticket-customer-doc {
  font-size: 14px;
}

.ticket-items {
  margin-bottom: 0.5rem;
}

.ticket-items-head {
  display: flex;
  font-weight: 700;
  font-size: 14px;
  gap: 0.25rem;
}

.th-num,
.td-num {
  width: 8%;
  text-align: center;
}

.th-qty,
.td-qty {
  width: 12%;
  text-align: center;
}

.th-desc,
.td-desc {
  flex: 1;
  text-align: left;
}

.th-total,
.td-total {
  width: 28%;
  text-align: right;
}

.th-sub {
  display: block;
  font-size: 11px;
  font-weight: 400;
}

.ticket-item-row {
  display: flex;
  font-size: 14px;
  gap: 0.25rem;
  margin-bottom: 0.25rem;
}

.ticket-totals {
  margin-bottom: 0.5rem;
}

.ticket-total-line {
  display: flex;
  justify-content: space-between;
  font-size: 14px;
  margin-bottom: 0.15rem;
}

.ticket-total-main {
  font-weight: 700;
  margin-top: 0.25rem;
}

.ticket-payment {
  margin-bottom: 0.5rem;
}

.ticket-payment-head,
.ticket-payment-row {
  display: flex;
  justify-content: space-between;
  font-size: 14px;
}

.ticket-payment-head {
  font-weight: 700;
}

.ticket-footer {
  margin-bottom: 0.5rem;
}

.ticket-seller {
  text-align: center;
  font-weight: 700;
  font-size: 14px;
  margin-bottom: 0.25rem;
}

.ticket-footer-block {
  border: 1px solid #000;
  padding: 0.35rem;
}

.ticket-footer-line {
  display: flex;
  justify-content: space-between;
  font-size: 14px;
  margin-bottom: 0.15rem;
}

.ticket-legal {
  text-align: center;
  font-size: 11px;
  margin-top: 0.5rem;
}

@media print {
  .no-print {
    display: none !important;
  }

  .pos-ticket-page {
    background: #fff;
    padding: 0;
    align-items: stretch;
  }

  .ticket {
    box-shadow: none;
    width: 80mm;
    max-width: 100%;
    margin: 0 auto;
  }

  @page {
    size: 80mm auto;
    margin: 0;
  }

  body {
    background: #fff;
  }
}
</style>
